'use client';

import { useState, useEffect } from 'react';
import type { EntityReliability, DependencyMetrics, ReliabilityTrend } from '@/lib/command-center/types';

interface ReliabilityScoreCardProps {
  entityId: string;
  entityName?: string;
  reliability?: EntityReliability | null;
  dependencies?: DependencyMetrics | null;
  onRefresh?: () => void;
  compact?: boolean;
}

const TREND_COLORS: Record<ReliabilityTrend, { bg: string; text: string; icon: string }> = {
  improving: { bg: 'bg-green-900/30', text: 'text-green-400', icon: '↑' },
  stable: { bg: 'bg-gray-700/30', text: 'text-gray-400', icon: '→' },
  degrading: { bg: 'bg-red-900/30', text: 'text-red-400', icon: '↓' },
};

const AVAILABILITY_COLORS = {
  high: { threshold: 0.99, bg: 'bg-green-500', text: 'text-green-400', label: 'Excellent' },
  good: { threshold: 0.95, bg: 'bg-green-600', text: 'text-green-500', label: 'Good' },
  fair: { threshold: 0.9, bg: 'bg-yellow-500', text: 'text-yellow-400', label: 'Fair' },
  poor: { threshold: 0, bg: 'bg-red-500', text: 'text-red-400', label: 'Poor' },
};

function getAvailabilityColor(score: number | null | undefined) {
  if (score === null || score === undefined) {
    return { bg: 'bg-gray-600', text: 'text-gray-400', label: 'Unknown' };
  }
  if (score >= AVAILABILITY_COLORS.high.threshold) return AVAILABILITY_COLORS.high;
  if (score >= AVAILABILITY_COLORS.good.threshold) return AVAILABILITY_COLORS.good;
  if (score >= AVAILABILITY_COLORS.fair.threshold) return AVAILABILITY_COLORS.fair;
  return AVAILABILITY_COLORS.poor;
}

function formatDuration(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return 'N/A';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
}

export function ReliabilityScoreCard({
  entityId,
  entityName,
  reliability,
  dependencies,
  onRefresh,
  compact = false,
}: ReliabilityScoreCardProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const availabilityColor = getAvailabilityColor(reliability?.availability_score);
  const trend = reliability?.trend;
  const trendStyle = trend ? TREND_COLORS[trend] : null;

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-surface/50 rounded-lg border border-border-muted">
        <div className={`w-3 h-3 rounded-full ${availabilityColor.bg}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-foreground truncate">{entityName || 'Entity'}</div>
          <div className="text-xs text-foreground-tertiary">
            {formatPercentage(reliability?.availability_score)} uptime
          </div>
        </div>
        {trendStyle && (
          <span className={`text-sm ${trendStyle.text}`}>{trendStyle.icon}</span>
        )}
        {dependencies?.is_single_point_of_failure && (
          <span className="px-1.5 py-0.5 text-xs bg-red-900/50 text-red-400 rounded">
            SPOF
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted">
        <h3 className="text-sm font-medium text-foreground">Reliability Metrics</h3>
        {onRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1.5 text-foreground-tertiary hover:text-foreground hover:bg-cream/10 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh metrics"
          >
            <svg
              className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Main metrics */}
      <div className="p-4 space-y-4">
        {/* Availability Score */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-foreground-tertiary">Availability</span>
            <span className={`text-sm font-medium ${availabilityColor.text}`}>
              {availabilityColor.label}
            </span>
          </div>
          <div className="h-2 bg-background rounded-full overflow-hidden">
            <div
              className={`h-full ${availabilityColor.bg} transition-all duration-500`}
              style={{ width: `${(reliability?.availability_score || 0) * 100}%` }}
            />
          </div>
          <div className="mt-1 text-right text-xs text-foreground-muted">
            {formatPercentage(reliability?.availability_score)}
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* MTBF */}
          <div className="p-3 bg-background/50 rounded-lg">
            <div className="text-xs text-foreground-tertiary mb-1">MTBF</div>
            <div className="text-lg font-semibold text-foreground">
              {formatDuration(reliability?.mtbf_hours)}
            </div>
            <div className="text-xs text-foreground-muted">Mean Time Between Failures</div>
          </div>

          {/* MTTR */}
          <div className="p-3 bg-background/50 rounded-lg">
            <div className="text-xs text-foreground-tertiary mb-1">MTTR</div>
            <div className="text-lg font-semibold text-foreground">
              {formatDuration(reliability?.mttr_hours)}
            </div>
            <div className="text-xs text-foreground-muted">Mean Time To Recovery</div>
          </div>
        </div>

        {/* Incident counts */}
        <div>
          <div className="text-xs text-foreground-tertiary mb-2">Incident History</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 text-center p-2 bg-background/50 rounded">
              <div className="text-lg font-semibold text-foreground">
                {reliability?.incident_count_7d || 0}
              </div>
              <div className="text-xs text-foreground-muted">7 days</div>
            </div>
            <div className="flex-1 text-center p-2 bg-background/50 rounded">
              <div className="text-lg font-semibold text-foreground">
                {reliability?.incident_count_30d || 0}
              </div>
              <div className="text-xs text-foreground-muted">30 days</div>
            </div>
            <div className="flex-1 text-center p-2 bg-background/50 rounded">
              <div className="text-lg font-semibold text-foreground">
                {reliability?.incident_count_90d || 0}
              </div>
              <div className="text-xs text-foreground-muted">90 days</div>
            </div>
          </div>
        </div>

        {/* Trend and uptime streak */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-tertiary">Trend:</span>
            {trendStyle ? (
              <span className={`px-2 py-0.5 text-xs rounded ${trendStyle.bg} ${trendStyle.text}`}>
                {trendStyle.icon} {trend}
              </span>
            ) : (
              <span className="text-xs text-foreground-muted">N/A</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-tertiary">Uptime streak:</span>
            <span className="text-sm text-foreground">
              {reliability?.uptime_streak_days !== undefined
                ? `${reliability.uptime_streak_days} days`
                : 'N/A'}
            </span>
          </div>
        </div>

        {/* Data quality indicator */}
        {reliability?.data_quality && (
          <div className="flex items-center gap-2 text-xs text-foreground-muted">
            <span>Data quality:</span>
            <span
              className={`px-1.5 py-0.5 rounded ${
                reliability.data_quality === 'high'
                  ? 'bg-green-900/30 text-green-400'
                  : reliability.data_quality === 'medium'
                  ? 'bg-yellow-900/30 text-yellow-400'
                  : reliability.data_quality === 'low'
                  ? 'bg-accent-900/30 text-accent-400'
                  : 'bg-gray-700/30 text-gray-400'
              }`}
            >
              {reliability.data_quality}
            </span>
          </div>
        )}
      </div>

      {/* Dependencies section */}
      {dependencies && (
        <div className="px-4 py-3 border-t border-border-muted bg-background/30">
          <div className="text-xs text-foreground-tertiary mb-2">Dependency Metrics</div>
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="text-foreground-tertiary">↑ Upstream:</span>{' '}
              <span className="text-foreground">{dependencies.upstream_count}</span>
            </div>
            <div>
              <span className="text-foreground-tertiary">↓ Downstream:</span>{' '}
              <span className="text-foreground">{dependencies.downstream_count}</span>
            </div>
            {dependencies.is_single_point_of_failure && (
              <span className="ml-auto px-2 py-0.5 text-xs bg-red-900/50 text-red-400 rounded-full border border-red-500/30">
                Single Point of Failure
              </span>
            )}
          </div>
          {dependencies.criticality_score !== null && dependencies.criticality_score !== undefined && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-foreground-tertiary">Criticality</span>
                <span className="text-foreground">{(dependencies.criticality_score * 100).toFixed(0)}%</span>
              </div>
              <div className="h-1.5 bg-background rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    dependencies.criticality_score > 0.7
                      ? 'bg-red-500'
                      : dependencies.criticality_score > 0.4
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${dependencies.criticality_score * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Last computed */}
      {reliability?.computed_at && (
        <div className="px-4 py-2 border-t border-border-muted text-xs text-foreground-muted">
          Last computed: {new Date(reliability.computed_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default ReliabilityScoreCard;
